if (!customElements.get("collection-slider")) {
  class CollectionSlider extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this.mobileBreakpoint = 750;

      this.handleResize =
        this.handleResize.bind(this);

      this.updateProgress =
        this.updateProgress.bind(this);

      this.handleMouseEnter =
        this.handleMouseEnter.bind(this);

      this.handleMouseLeave =
        this.handleMouseLeave.bind(this);
    }

    connectedCallback() {
      this.slider = this.querySelector(
        "[data-collection-slider-swiper]"
      );

      if (!this.slider) return;

      this.prevButton = this.querySelector(
        "[data-collection-slider-prev]"
      );

      this.nextButton = this.querySelector(
        "[data-collection-slider-next]"
      );

      this.pagination = this.querySelector(
        "[data-collection-slider-pagination]"
      );

      this.progress = this.querySelector(
        "[data-collection-slider-progress]"
      );

      this.progressFill = this.querySelector(
        "[data-collection-slider-progress-fill]"
      );

      this.mobileSliderEnabled =
        this.dataset.enableMobileSlider === "true";

      this.showMobileProgress =
        this.dataset.showMobileProgress === "true";

      this.pauseOnHover =
        this.dataset.pauseOnHover === "true";

      window.addEventListener(
        "resize",
        this.handleResize
      );

      this.initSlider();

      if (this.pauseOnHover) {
        this.addEventListener(
          "mouseenter",
          this.handleMouseEnter
        );

        this.addEventListener(
          "mouseleave",
          this.handleMouseLeave
        );
      }
    }

    disconnectedCallback() {
      window.removeEventListener(
        "resize",
        this.handleResize
      );

      this.removeEventListener(
        "mouseenter",
        this.handleMouseEnter
      );

      this.removeEventListener(
        "mouseleave",
        this.handleMouseLeave
      );

      this.destroySlider();
    }

    isMobile() {
      return window.innerWidth < this.mobileBreakpoint;
    }

    shouldUseSlider() {
      if (!this.isMobile()) {
        return true;
      }

      return this.mobileSliderEnabled;
    }

    initSlider() {
      if (!this.shouldUseSlider()) {
        this.destroySlider();
        return;
      }

      if (this.swiper) return;

      if (typeof window.Swiper !== "function") {
        window.setTimeout(() => {
          this.initSlider();
        }, 100);

        return;
      }

      const mobileColumns =
        Number(this.dataset.slidesMobile) || 2;

      const mobileRows =
        Number(this.dataset.mobileRows) || 1;

      const desktopSlides =
        Number(this.dataset.slidesDesktop) || 5;

      const tabletSlides =
        Number(this.dataset.slidesTablet) || 3;

      const gapDesktop =
        Number(this.dataset.gapDesktop) || 12;

      const gapMobile =
        Number(this.dataset.gapMobile) || 10;

      const speed =
        Number(this.dataset.slideSpeed) || 500;

      const enableDrag =
        this.dataset.enableDrag === "true";

      const enableKeyboard =
        this.dataset.enableKeyboard === "true";

      const enableLoop =
        this.dataset.enableLoop === "true";

      const autoRotate =
        this.dataset.autoRotate === "true";

      const autoplayDelay =
        Number(this.dataset.autoRotateDelay) || 5000;

      /*
       * Loop intentionally disable on 2-row mobile grid.
       * Swiper grid + loop can produce duplicated / reordered
       * slides depending on item count.
       */
      const canLoop =
        enableLoop &&
        !(this.isMobile() && mobileRows > 1);

      const options = {
        slidesPerView: mobileColumns,
        slidesPerGroup: mobileColumns,
        spaceBetween: gapMobile,
        speed: speed,

        loop: canLoop,

        watchOverflow: true,

        observer: true,
        observeParents: true,
        observeSlideChildren: true,

        resizeObserver: true,

        grabCursor: enableDrag,
        allowTouchMove: enableDrag,
        simulateTouch: enableDrag,

        keyboard: enableKeyboard
          ? {
              enabled: true,
              onlyInViewport: true
            }
          : false,

        navigation: {
          nextEl: this.nextButton,
          prevEl: this.prevButton
        },

        pagination: this.pagination
          ? {
              el: this.pagination,
              clickable: true
            }
          : false,

        breakpoints: {
          0: {
            slidesPerView: mobileColumns,
            slidesPerGroup: mobileColumns,
            spaceBetween: gapMobile,

            grid: {
              rows: mobileRows,
              fill: "row"
            }
          },

          750: {
            slidesPerView: tabletSlides,
            slidesPerGroup: 1,
            spaceBetween: gapDesktop,

            grid: {
              rows: 1
            }
          },

          990: {
            slidesPerView: desktopSlides,
            slidesPerGroup: 1,
            spaceBetween: gapDesktop,

            grid: {
              rows: 1
            }
          }
        },

        on: {
          init: () => {
            requestAnimationFrame(
              this.updateProgress
            );
          },

          slideChange: () => {
            this.updateProgress();
          },

          progress: () => {
            this.updateProgress();
          },

          resize: () => {
            this.updateProgress();
          },

          breakpoint: () => {
            this.updateProgress();
          }
        }
      };

      if (autoRotate) {
        options.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: false
        };
      }

      this.swiper = new window.Swiper(
        this.slider,
        options
      );

      this.updateProgress();
    }

    destroySlider() {
      if (!this.swiper) return;

      this.swiper.destroy(
        true,
        true
      );

      this.swiper = null;

      if (this.progressFill) {
        this.progressFill.style.width = "";
        this.progressFill.style.transform = "";
      }
    }

    handleResize() {
      const shouldUseSlider =
        this.shouldUseSlider();

      if (
        shouldUseSlider &&
        !this.swiper
      ) {
        this.initSlider();
        return;
      }

      if (
        !shouldUseSlider &&
        this.swiper
      ) {
        this.destroySlider();
        return;
      }

      if (this.swiper) {
        this.swiper.update();

        requestAnimationFrame(
          this.updateProgress
        );
      }
    }

    updateProgress() {
      if (
        !this.swiper ||
        !this.progressFill ||
        !this.showMobileProgress ||
        !this.isMobile()
      ) {
        return;
      }

      const totalSlides =
        Number(this.dataset.slideCount) || 0;

      const columns =
        Number(this.dataset.slidesMobile) || 2;

      const rows =
        Number(this.dataset.mobileRows) || 1;

      const itemsPerPage =
        Math.max(
          1,
          columns * rows
        );

      const totalPages =
        Math.max(
          1,
          Math.ceil(
            totalSlides / itemsPerPage
          )
        );

      /*
       * Swiper progress is 0 → 1 regardless of amount
       * of cards, so thumb width represents one visible page.
       */

      const thumbPercent =
        Math.min(
          100,
          100 / totalPages
        );

      const availableTravel =
        100 - thumbPercent;

      const swiperProgress =
        Number.isFinite(this.swiper.progress)
          ? Math.min(
              1,
              Math.max(
                0,
                this.swiper.progress
              )
            )
          : 0;

      const translatePercent =
        availableTravel * swiperProgress;

      this.progressFill.style.width =
        `${thumbPercent}%`;

      this.progressFill.style.transform =
        `translateX(${
          thumbPercent > 0
            ? (translatePercent / thumbPercent) * 100
            : 0
        }%)`;
    }

    handleMouseEnter() {
      if (
        this.swiper?.autoplay?.running
      ) {
        this.swiper.autoplay.pause();
      }
    }

    handleMouseLeave() {
      if (
        this.swiper?.autoplay
      ) {
        this.swiper.autoplay.resume();
      }
    }
  }

  customElements.define(
    "collection-slider",
    CollectionSlider
  );
}